import type { CSSProperties } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { DevTools } from '../components/DevTools';
import { useAppSettings } from '../hooks/useAppSettings';
import { useRemoteNavigation } from '../hooks/useRemoteNavigation';
import { useViewportHeight } from '../hooks/useViewportHeight';
import { AccountsPage } from '../pages/Accounts/AccountsPage';
import { ChannelPage } from '../pages/Channel/ChannelPage';
import { ChannelsPage } from '../pages/Channels/ChannelsPage';
import { accountsPath, legacyPathFromSearch } from './routes';

const LegacyEntry = () => {
  const { search } = useLocation();
  const legacyPath = legacyPathFromSearch(search);
  return legacyPath ? <Navigate to={legacyPath} replace /> : <AccountsPage />;
};

const LegacyIndexEntry = () => {
  const { search } = useLocation();
  const legacyPath = legacyPathFromSearch(search);
  return <Navigate to={legacyPath || accountsPath} replace />;
};

const AppRoutes = () => {
  const settings = useAppSettings();
  const viewportHeight = useViewportHeight();
  useRemoteNavigation();

  const rootStyle = {
    height: `${viewportHeight}px`,
    minHeight: `${viewportHeight}px`,
  } as CSSProperties;

  return (
    <div
      className={`${settings.isTv ? 'theme-tv' : 'theme-dark'} relative w-full overflow-hidden bg-app-background text-app-text`}
      style={rootStyle}
      data-device={settings.device}
    >
      <DevTools />
      <Routes>
        <Route path="/" element={<LegacyEntry />} />
        <Route path="/index.html" element={<LegacyIndexEntry />} />
        <Route path="/accounts/*" element={<Navigate to="/" replace />} />
        <Route path="/:env/:accountId/:channelId" element={<ChannelPage />} />
        <Route path="/:env/:accountId" element={<ChannelsPage />} />
        <Route path="/:env" element={<AccountsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

const getBasename = (): string | undefined => {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  return base || undefined;
};

export const App = () => (
  <BrowserRouter basename={getBasename()}>
    <AppRoutes />
  </BrowserRouter>
);
